// FASE 05 — S4-B5 (plano docs/19-08-2026): alteracao de precos em massa.
// Espec. secao 6 do mockup 29. O reajuste incide apenas sobre itens ja precificados —
// aplicar percentual sobre preco zero nao produziria efeito.

import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OperacaoMassa, OPERACAO_MASSA_LABELS } from "../../types/tabelaPreco.types";

interface ModalAlterarPrecosProps {
  open: boolean;
  quantidadeSelecionada: number;
  onClose: () => void;
  onAplicar: (operacao: OperacaoMassa, valor: number) => Promise<void>;
}

export const ModalAlterarPrecos: React.FC<ModalAlterarPrecosProps> = ({
  open,
  quantidadeSelecionada,
  onClose,
  onAplicar,
}) => {
  const [operacao, setOperacao] = useState<OperacaoMassa>("percentual");
  const [valor, setValor] = useState("");
  const [erro, setErro] = useState("");
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    if (!open) {
      setOperacao("percentual");
      setValor("");
      setErro("");
    }
  }, [open]);

  const handleAplicar = async () => {
    const numero = Number(String(valor).replace(",", "."));

    if (!valor.trim() || Number.isNaN(numero)) {
      setErro("Informe um valor numérico.");
      return;
    }
    if (operacao === "multiplicar" && numero <= 0) {
      setErro("O fator de multiplicação precisa ser maior que zero.");
      return;
    }

    setAplicando(true);
    try {
      await onAplicar(operacao, numero);
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Dialog open={open} onClose={aplicando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Alteração de preços em massa
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={aplicando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            {quantidadeSelecionada > 0
              ? `O reajuste será aplicado aos ${quantidadeSelecionada} item(ns) selecionado(s).`
              : "Nenhum item selecionado — o reajuste será aplicado a todos os produtos já precificados desta tabela."}
          </Alert>

          <FormControl fullWidth disabled={aplicando}>
            <InputLabel>Operação</InputLabel>
            <Select
              value={operacao}
              label="Operação"
              onChange={(e) => setOperacao(e.target.value as OperacaoMassa)}
            >
              {(Object.keys(OPERACAO_MASSA_LABELS) as OperacaoMassa[]).map((chave) => (
                <MenuItem key={chave} value={chave}>
                  {OPERACAO_MASSA_LABELS[chave]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={operacao === "percentual" ? "Percentual (%)" : "Valor"}
            value={valor}
            onChange={(e) => {
              setValor(e.target.value);
              if (erro) setErro("");
            }}
            error={!!erro}
            helperText={
              erro ||
              (operacao === "percentual"
                ? "Use valor negativo para reduzir. Ex.: 10 aumenta 10%, -5 reduz 5%."
                : " ")
            }
            disabled={aplicando}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={aplicando} sx={{ color: "#666", borderColor: "#bdbdbd" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleAplicar}
          variant="contained"
          disabled={aplicando}
          startIcon={aplicando ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
